import { z } from "zod";

export const CURRENT_BOARD_SCHEMA_VERSION = 1;

const jsonObjectSchema = z.record(z.string(), z.json());

const viewportSchema = z
  .object({
    scrollX: z.number().finite(),
    scrollY: z.number().finite(),
    zoom: z.number().finite().positive(),
  })
  .loose();

const editorSchema = z
  .object({
    engine: z.literal("excalidraw"),
    engineVersion: z.string().min(1),
    elements: z.array(jsonObjectSchema),
    appState: jsonObjectSchema,
    viewport: viewportSchema,
  })
  .loose();

export const assetIdSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);

const assetSchema = z
  .object({
    path: z.string().min(1),
    mimeType: z.enum(["image/png", "image/jpeg", "image/webp", "image/gif"]),
    byteLength: z.number().int().nonnegative(),
    originalName: z.string().min(1),
  })
  .loose();

export const boardDocumentSchema = z
  .object({
    format: z.literal("shotboard-board"),
    schemaVersion: z.literal(CURRENT_BOARD_SCHEMA_VERSION),
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    editor: editorSchema,
    assets: z.record(assetIdSchema, assetSchema),
  })
  .loose();

export type BoardDocument = z.infer<typeof boardDocumentSchema>;
export type BoardAsset = z.infer<typeof assetSchema>;
export type BoardEditorState = z.infer<typeof editorSchema>;

export type BoardParseResult =
  | { readonly kind: "valid"; readonly document: BoardDocument }
  | {
      readonly kind: "migration";
      readonly fromVersion: 0;
      readonly document: BoardDocument;
      readonly raw: string;
    }
  | { readonly kind: "newer"; readonly schemaVersion: number; readonly raw: string }
  | { readonly kind: "invalid"; readonly reason: string; readonly raw: string };

const headerSchema = z.object({
  format: z.literal("shotboard-board"),
  schemaVersion: z.number().int().nonnegative(),
});

const legacyBoardV0Schema = z
  .object({
    format: z.literal("shotboard-board"),
    schemaVersion: z.literal(0),
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    engineVersion: z.string().min(1),
    elements: z.array(jsonObjectSchema),
    appState: jsonObjectSchema,
    assets: z.record(assetIdSchema, assetSchema),
  })
  .loose();

/** Parses untrusted board JSON without rewriting invalid or newer content. */
export function parseBoardDocument(raw: string): BoardParseResult {
  let value: unknown;

  try {
    value = JSON.parse(raw);
  } catch {
    return { kind: "invalid", reason: "board.json is not valid JSON.", raw };
  }

  const header = headerSchema.safeParse(value);
  if (!header.success) {
    return { kind: "invalid", reason: "board.json has an invalid format header.", raw };
  }

  if (header.data.schemaVersion > CURRENT_BOARD_SCHEMA_VERSION) {
    return { kind: "newer", schemaVersion: header.data.schemaVersion, raw };
  }

  if (header.data.schemaVersion === 0) {
    const legacy = legacyBoardV0Schema.safeParse(value);
    if (!legacy.success) {
      return { kind: "invalid", reason: z.prettifyError(legacy.error), raw };
    }
    return {
      kind: "migration",
      fromVersion: 0,
      document: migrateBoardV0(legacy.data),
      raw,
    };
  }

  const document = boardDocumentSchema.safeParse(value);
  if (!document.success) {
    return { kind: "invalid", reason: z.prettifyError(document.error), raw };
  }

  return { kind: "valid", document: document.data };
}

/** Migrates the pre-envelope editor fields into the version-one editor boundary. */
function migrateBoardV0(legacy: z.infer<typeof legacyBoardV0Schema>): BoardDocument {
  const { appState, elements, engineVersion, ...retained } = legacy;
  return boardDocumentSchema.parse({
    ...retained,
    schemaVersion: CURRENT_BOARD_SCHEMA_VERSION,
    editor: {
      engine: "excalidraw",
      engineVersion,
      elements,
      appState,
      viewport: { scrollX: 0, scrollY: 0, zoom: 1 },
    },
  });
}

/** Serializes a validated document with stable human-readable indentation. */
export function serializeBoardDocument(document: BoardDocument): string {
  return `${JSON.stringify(boardDocumentSchema.parse(document), null, 2)}\n`;
}
